import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Component, ComponentRef, Injectable, Input } from '@angular/core';

/**
 * Voile de chargement. Remplace @ff/loader (LoaderComponent + LoaderService),
 * logique inchangee.
 *
 * Le composant et son service sont dans le meme fichier, donc le template et
 * les styles sont en ligne (`template` / `styles`) plutot qu'en .html et .scss
 * separes. Le budget anyComponentStyle d'angular.json est de 6 ko, on est tres
 * en dessous.
 */

/**
 * Ce composant n'apparait dans aucun template : MpsLoaderService l'instancie
 * par programme via un ComponentPortal du CDK Overlay. En ViewEngine
 * (enableIvy: false dans tsconfig.json) cela impose qu'il soit declare dans un
 * NgModule et liste dans ses entryComponents, ce qui est fait dans
 * app/modules/shared/shared.module.ts. Retirer cette entree ferait echouer
 * l'overlay a l'execution avec "No component factory found".
 *
 * Le selecteur d'origine `ff-loader` devient `mps-loader`, comme dans
 * resi-front. Le prefixe `mps` est declare a cote de `app` dans tslint.json,
 * sinon la regle component-selector refuserait tout composant de src/mps/.
 *
 * Les declarations prefixees des styles (-webkit-box, -webkit-box-align,
 * -webkit-box-pack) sont des sorties d'autoprefixer du CSS compile livre par
 * @ff/loader, conservees telles quelles : les retirer reviendrait a laisser la
 * browserslist de l'application decider a nouveau, ce qui peut changer le
 * rendu sur les navigateurs anciens.
 */
@Component({
    selector: 'mps-loader',
    template: `<mat-progress-spinner [mode]="mode" [value]="value"></mat-progress-spinner>`,
    styles: [`
        :host {
            position: fixed;
            width: 100%;
            z-index: 9999;
            top: 0;
            left: 0;
            min-height: 100%;
            height: 100%;
            display: -webkit-box;
            display: flex;
            -webkit-box-align: center;
            align-items: center;
            -webkit-box-pack: center;
            justify-content: center;
            background-color: rgba(255, 255, 255, .5);
        }
    `]
})
export class MpsLoaderComponent {

    @Input() public mode: string;
    @Input() public value: number;

}

/**
 * Fournit show() / hide() / isVisible(), utilises dans une dizaine de
 * composants. add() n'est appele nulle part aujourd'hui mais reste expose.
 *
 * Fourni dans app/modules/shared/shared.module.ts, et non a la racine, pour
 * conserver exactement la portee d'injection qu'avait le NgModule @ff/loader.
 */
// @dynamic
@Injectable()
export class MpsLoaderService {

    private _overlayRef: OverlayRef;
    private _portal: ComponentPortal<MpsLoaderComponent>;
    private _componentRef: ComponentRef<MpsLoaderComponent>;

    public constructor(
        private _overlay: Overlay
    ) {
        this._overlayRef = _overlay.create();
        this._portal = new ComponentPortal(MpsLoaderComponent);
    }

    public add(value: number) {

        this.show();

        this._componentRef.instance.mode = 'determinate';
        this._componentRef.instance.value += value;

        if (this._componentRef.instance.value >= 100) {
            this._componentRef.instance.value = 0;
            this.hide();
        }
    }

    private _reset() {
        if (this._componentRef) {
            this._componentRef.instance.mode = 'indeterminate';
            this._componentRef.instance.value = 0;
        }
    }

    public show() {
        if (!this._portal.isAttached) {
            this._componentRef = this._overlayRef.attach(this._portal);
            this._reset();
        }
    }

    public hide() {
        if (this._componentRef) {
            this._overlayRef.detach();
            this._componentRef = null;
        }
    }

    public isVisible() {
        return this._componentRef;
    }
}
